/**
 * Module registry.
 *
 * Three things to know before adding a module:
 *
 * 1. `id` is the permanent internal key and the Firestore progress doc id
 *    (`.../progress/{id}`). Never renumber an existing one — that orphans
 *    every trainee's progress.
 * 1b. `slug` is the module's ADDRESS: `/courses/<course>/module/<slug>`. It is
 *    a second name for the same record, never an identity — `getModule`
 *    resolves the id first, because a slug shadowing an id would open the
 *    wrong module and write to the wrong progress document. It must be unique
 *    against every other slug AND every id. An id still resolves, so old links
 *    keep working.
 * 2. `num` is what the trainee sees ("Module 3"). It restarts at 1 for each
 *    track so a Finance starter isn't told to begin at "Module 17".
 * 3. A module must also appear in a `phases[].modules` array below, or it
 *    never renders on /modules.
 *
 * `optional: true` keeps a module out of the completion denominator — skipping
 * the install walkthrough should not cap someone at 88% forever.
 * `published: false` registers a module without showing it to trainees yet.
 */

export const SEED_COURSES = [
  {
    id: 'sheets',
    name: 'Sheets Reporting',
    description: 'Build clean, automated reports from raw data.',
  },
  {
    id: 'claude-basics',
    name: 'Getting Started with Claude',
    description:
      'Get Claude running on your machine, and understand what it actually is before you point it at your work.',
  },
  {
    id: 'apps-script',
    name: 'Apps Script',
    description:
      'Write code that lives in your spreadsheet — menus, custom functions and jobs that run without you. Nothing here needs Claude.',
  },
  {
    id: 'ai-finance',
    name: 'AI with Claude for Finance',
    description:
      'Get Claude to do the work in Google Sheets — and know when to make it write a script instead.',
  },
]

export const modules = [
  {
    id: 0,
    num: 0,
    track: 'sheets',
    slug: 'data-sources',
    title: 'Where Does Our Data Come From?',
    subtitle: 'Understanding the data pipeline before touching a formula',
    phase: 'The Big Picture',
    phaseColor: 'amber',
    description: 'Learn how clicks flow from the website through HasOffers and BigQuery, and how conversions arrive via postbacks or client reports.',
  },
  {
    id: 1,
    num: 1,
    track: 'sheets',
    slug: 'cells-ranges',
    title: 'Cells, Ranges & References',
    subtitle: 'The building blocks of every formula',
    phase: 'Foundations',
    phaseColor: 'blue',
    description: 'What cell addresses mean, how ranges work, and the difference between relative and absolute references.',
  },
  {
    id: 2,
    num: 2,
    track: 'sheets',
    slug: 'thinking-in-tables',
    title: 'Thinking in Tables',
    subtitle: 'Your spreadsheet is a database',
    phase: 'Foundations',
    phaseColor: 'blue',
    description: 'Why structured tables with consistent headers are the foundation of everything. Rows are records, columns are fields.',
  },
  {
    id: 3,
    num: 3,
    track: 'sheets',
    slug: 'keys-relationships',
    title: 'Keys & Relationships',
    subtitle: 'How two tables connect',
    phase: 'Foundations',
    phaseColor: 'blue',
    description: 'What makes a row unique, composite keys like Offer ID + Date, and how separate tables relate through shared keys.',
  },
  {
    id: 4,
    num: 4,
    track: 'sheets',
    slug: 'basic-formulas',
    title: 'Basic Arithmetic & Aggregation',
    subtitle: 'SUM, COUNT, AVERAGE, and conversion rate',
    phase: 'Core Formulas',
    phaseColor: 'green',
    description: 'The fundamental formulas: adding, counting, averaging. Calculate conversion rate and handle division by zero.',
  },
  {
    id: 5,
    num: 5,
    track: 'sheets',
    slug: 'vlookup',
    title: 'Looking Up Data (VLOOKUP)',
    subtitle: 'Find a value in another table by its key',
    phase: 'Core Formulas',
    phaseColor: 'green',
    description: 'VLOOKUP explained visually — what each argument means, how the lookup walks down a column to find a match.',
  },
  {
    id: 6,
    num: 6,
    track: 'sheets',
    slug: 'index-match',
    title: 'Looking Up Data (INDEX/MATCH)',
    subtitle: 'The flexible alternative to VLOOKUP',
    phase: 'Core Formulas',
    phaseColor: 'green',
    description: 'INDEX finds a value at a position, MATCH finds the position. Together they can look in any direction.',
  },
  {
    id: 7,
    num: 7,
    track: 'sheets',
    slug: 'sumifs-countifs',
    title: 'SUMIFS & COUNTIFS',
    subtitle: 'Add up or count values with conditions',
    phase: 'Core Formulas',
    phaseColor: 'green',
    description: 'SUMIFS totals values where conditions are met. COUNTIFS counts matching rows. When to use these vs VLOOKUP vs INDEX/MATCH.',
  },
  {
    id: 8,
    num: 8,
    track: 'sheets',
    slug: 'filter',
    title: 'Filtering Data',
    subtitle: 'Extract exactly the rows you need',
    phase: 'Working at Scale',
    phaseColor: 'purple',
    description: 'The FILTER function — give me all rows where this condition is true. Way better than building manual tables.',
  },
  {
    id: 9,
    num: 9,
    track: 'sheets',
    slug: 'query',
    title: 'QUERY — Your Mini Database',
    subtitle: 'Ask questions of your data in plain English',
    phase: 'Working at Scale',
    phaseColor: 'purple',
    description: 'SELECT, WHERE, ORDER BY — like a database query language built right into Google Sheets.',
  },
  {
    id: 10,
    num: 10,
    track: 'sheets',
    slug: 'combining-formulas',
    title: 'Combining Data with Formulas',
    subtitle: 'VLOOKUP, INDEX/MATCH, and QUERY to join two tables',
    phase: 'Working at Scale',
    phaseColor: 'purple',
    description: 'The practical formulas for pulling conversions into your clicks table: composite keys, VLOOKUP, INDEX/MATCH, QUERY, and handling mismatches with IFERROR.',
  },
  {
    id: 11,
    num: 11,
    track: 'sheets',
    slug: 'combining-data',
    title: 'Combining Two Datasets',
    subtitle: 'Joining clicks and conversions into one table',
    phase: 'Working at Scale',
    phaseColor: 'purple',
    description: 'The big payoff: bring clicks from BigQuery and conversions from postbacks/reports into a single combined dataset.',
  },
  {
    id: 12,
    num: 12,
    track: 'sheets',
    slug: 'building-report',
    title: 'From Combined Data to Report',
    subtitle: 'Extracting a clean per-offer view',
    phase: 'The Real Workflow',
    phaseColor: 'red',
    description: 'Use FILTER or QUERY on your combined dataset to pull out one offer\'s data, formatted and ready for the client.',
  },
  {
    id: 13,
    num: 13,
    track: 'sheets',
    slug: 'importrange',
    title: 'IMPORTRANGE — Power & Danger',
    subtitle: 'Pulling data across spreadsheets safely',
    phase: 'The Real Workflow',
    phaseColor: 'red',
    description: 'How IMPORTRANGE connects spreadsheets, why it\'s powerful, and why pointing it at your master data is dangerous.',
  },
  {
    id: 14,
    num: 14,
    track: 'sheets',
    slug: 'bridge-sheets',
    title: 'The Bridge Sheet Pattern',
    subtitle: 'The security architecture for client reports',
    phase: 'The Real Workflow',
    phaseColor: 'red',
    description: 'Master → Bridge → Client. Two IMPORTRANGEs, zero risk. The safe way to give clients live-updating reports.',
  },
  {
    id: 15,
    num: 15,
    track: 'sheets',
    slug: 'capstone',
    title: 'Putting It All Together',
    subtitle: 'End-to-end: raw data to client report',
    phase: 'The Real Workflow',
    phaseColor: 'red',
    description: 'Build the full pipeline from scratch. Raw clicks + conversions → combined dataset → bridge → client sheet.',
  },
  {
    id: 16,
    num: 16,
    track: 'sheets',
    slug: 'troubleshooting',
    title: 'Troubleshooting Someone Else\'s Sheet',
    subtitle: 'A systematic approach to finding and fixing problems',
    phase: 'Maintaining & Debugging',
    phaseColor: 'cyan',
    description: 'How to orient yourself in a sheet you didn\'t build, trace data to its source, identify manual data problems, and fix common errors.',
  },

  /* ─── AI with Claude ─── */

  {
    id: 17,
    num: 1,
    track: 'claude-basics',
    slug: 'getting-set-up',
    title: 'Getting Set Up',
    subtitle: 'Get Claude running on your machine',
    phase: 'Getting Started with Claude',
    phaseColor: 'indigo',
    optional: true,
    published: true,
    // Declared, not inferred. This module asks nothing — its steps live in the
    // Confluence guides — so it completes without a score. Deciding that from
    // the ABSENCE of questions fails open: any lesson that lost its questions
    // in a bad edit would silently become an evidence-free completion instead
    // of erroring. PagedLesson requires this flag before it will do that.
    readThrough: true,
    description: 'Which machine you have decides which path you take. Skip this if Claude already runs for you — and stop and ask for help if you get stuck, rather than fighting it.',
  },
  {
    id: 18,
    num: 2,
    track: 'claude-basics',
    slug: 'what-claude-is',
    title: 'What Claude Actually Is',
    subtitle: 'An agent, not a chatbot',
    phase: 'Getting Started with Claude',
    phaseColor: 'indigo',
    published: true,
    description: 'It reads, runs and edits real things. What that means for approval prompts, what never goes in a chat box, and the habits that get good results.',
  },
  {
    id: 26,
    num: 3,
    track: 'claude-basics',
    slug: 'github-101',
    title: 'GitHub 101: Repos, Branches & Syncing',
    subtitle: 'Where code lives, how branches protect production, and the push/pull loop',
    phase: 'Getting Started with Claude',
    phaseColor: 'indigo',
    published: true,
    description: 'Understand Git repositories, why we create branches, where files live (local vs cloud), and how push/pull keeps teams in sync.',
  },
  {
    id: 19,
    num: 1,
    track: 'ai-finance',
    slug: 'your-first-ask',
    title: 'Your First Ask',
    subtitle: 'One cell, and the loop that matters',
    phase: 'Sheets with Claude',
    phaseColor: 'teal',
    published: true,
    description: 'Make a blank Google Sheet and ask Claude to write one thing into one cell. Small on purpose — what you are watching is the loop.',
  },
  {
    id: 25,
    num: 2,
    track: 'ai-finance',
    slug: 'ask-for-the-data',
    title: 'Ask for the Data',
    subtitle: "Describe the table, don't type it",
    phase: 'Sheets with Claude',
    phaseColor: 'teal',
    published: true,
    description: 'Describe the register you want and have Claude write the Apps Script that builds it. Forty rows you never typed, and a recipe you can run again.',
  },
  {
    id: 20,
    num: 3,
    track: 'ai-finance',
    slug: 'prompting-for-sheet-work',
    title: 'Prompting for Sheet Work',
    subtitle: 'Specific in, great out',
    phase: 'Sheets with Claude',
    phaseColor: 'teal',
    published: true,
    description: 'Add columns, sort, format and summarise — and see exactly how much the wording of your request changes what you get back.',
  },
  {
    id: 21,
    num: 4,
    track: 'ai-finance',
    slug: 'when-ai-is-wrong-tool',
    title: 'When the LLM Is the Wrong Tool',
    subtitle: 'Ask twice, get two answers',
    phase: 'Sheets with Claude',
    phaseColor: 'teal',
    published: false,
    description: 'The pivot: the same calculation done by asking versus done by a formula. Why anything you have to defend later needs to be deterministic.',
  },
  {
    id: 24,
    num: 5,
    track: 'ai-finance',
    slug: 'the-local-loop',
    title: 'The Local Loop',
    subtitle: 'Where the code really lives',
    phase: 'Going Further',
    phaseColor: 'rose',
    optional: true,
    published: false,
    description: 'Bonus for the curious: editing Apps Script on your own machine, why the web editor is not the source of truth, and how versions get kept.',
  },
  {
    id: 22,
    num: 1,
    track: 'apps-script',
    slug: 'first-apps-script',
    title: 'Your First Apps Script',
    subtitle: 'Code you did not write, running in your sheet',
    phase: 'Automating with Apps Script',
    phaseColor: 'rose',
    published: true,
    description: 'Somebody hands you a script — now what? Open the editor, read it, run it, approve the permission box, then change a line and watch what happens.',
  },
  {
    id: 23,
    num: 2,
    track: 'apps-script',
    slug: 'making-it-repeatable',
    title: 'Making It Repeatable',
    subtitle: 'A custom function, a menu, and a daily trigger',
    phase: 'Automating with Apps Script',
    phaseColor: 'rose',
    published: true,
    description: 'Turn a script you have to run into one nobody has to: a function you can call from a cell, a menu anyone can click, and a job that runs at 7am.',
  },
]

export const phases = [
  { name: 'The Big Picture', color: 'amber', track: 'sheets', modules: [0] },
  { name: 'Foundations', color: 'blue', track: 'sheets', modules: [1, 2, 3] },
  { name: 'Core Formulas', color: 'green', track: 'sheets', modules: [4, 5, 6, 7] },
  { name: 'Working at Scale', color: 'purple', track: 'sheets', modules: [8, 9, 10, 11] },
  { name: 'The Real Workflow', color: 'red', track: 'sheets', modules: [12, 13, 14, 15] },
  { name: 'Maintaining & Debugging', color: 'cyan', track: 'sheets', modules: [16] },

  { name: 'Getting Started with Claude', color: 'indigo', track: 'claude-basics', modules: [17, 18, 26] },
  { name: 'Sheets with Claude', color: 'teal', track: 'ai-finance', modules: [19, 25, 20, 21] },
  { name: 'Going Further', color: 'rose', track: 'ai-finance', modules: [24] },
  { name: 'Automating with Apps Script', color: 'rose', track: 'apps-script', modules: [22, 23] },
]

/** Look a track up by id, supporting custom SEED_COURSES. */
export function getCourse(id, customCourses = []) {
  if (!id) return undefined
  const found = SEED_COURSES.find((t) => t.id === id) || (customCourses || []).find((t) => t.id === id)
  if (found) return found
  const titleCase = String(id).charAt(0).toUpperCase() + String(id).slice(1).replace(/-/g, ' ')
  return {
    id: String(id),
    name: titleCase,
    description: `${titleCase} training track`,
    isCustom: true,
  }
}

/** Look a module up by its permanent id, supporting custom modules. */
export function getModule(idOrSlug, customModules = []) {
  if (idOrSlug === undefined || idOrSlug === null) return undefined
  const key = String(idOrSlug)
  const all = [...modules, ...(customModules || [])]
  // ID FIRST, ALWAYS. The id is the module's identity — the progress document
  // is keyed on it — so an id must never be shadowed by something else's slug.
  // The slug is a second address for the same thing, which is what lets a URL
  // read `/module/data-sources` while the record underneath stays `0`.
  return all.find((m) => String(m.id) === key) || all.find((m) => m.slug && String(m.slug) === key)
}

/** Published modules only — what a trainee is allowed to see. */
export function publishedModules(track, customModules = []) {
  const std = modules.filter(
    (m) => m.published !== false && (track ? m.track === track : true)
  )
  const custom = (customModules || []).filter(
    (m) => m.status === 'published' && (track ? m.track === track : true)
  )
  return [...std, ...custom]
}

/**
 * Modules that count towards a track's completion percentage: published,
 * and not flagged optional.
 */
export function requiredModules(track) {
  return publishedModules(track).filter((m) => !m.optional)
}

/**
 * What a trainee sees on the card: "Module 3". Falls back to the id, which is
 * the same number for every built-in module.
 *
 * Null when there is no number to show. A module authored in AI Studio has no
 * `num` and a non-numeric id, so the fallback produced the label "Module
 * custom-mtaw6809" — an internal key, shown to a trainee, in the place a
 * position in the course belongs. Callers omit the label when this is null
 * rather than rendering an empty "Module ".
 */
export function moduleNumber(mod) {
  if (mod?.num !== undefined && mod?.num !== null) return mod.num
  const id = mod?.id
  if (id === undefined || id === null || id === '') return null
  return Number.isFinite(Number(id)) ? id : null
}

/* ─── Degrees live in data/degrees.js ─── */

// They used to be a constant here. Creating one now means an admin editing a
// Firestore document rather than an engineer editing this file, so the shape,
// the defaults and the resolution logic moved to data/degrees.js — which
// imports this file for the course registry, not the other way around.

